import React, { useEffect } from "react";
import { RootRouteProps } from "routers/types/stack";
import { useRoute } from "@react-navigation/native";

import { View, ScrollView } from "react-native";
import {
  Input,
  Button,
  Picker,
  TextArea,
  InputLabel,
  DatePicker,
  RadioButtonGroup,
} from "components";
import { getFormatedDate } from "react-native-modern-datepicker";
import { useForm, Controller } from "react-hook-form";

import { colors, patterns, sizes, strings } from "assets/constants";

import { useCustomerCredits, useEditCustomer } from "clients/apis";
import { getPostData } from "../helper";

import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import css from "styles";
import { mq, sn } from "utils";
import { useChmod } from "hooks";
import { _goToBack } from "routers/helper";

export default function CreditInfo() {
  const { chmod } = useChmod();

  const { role, id: userId } = useRecoilValue(authAtom);

  const { data } = useRoute<RootRouteProps<"customerTabs">>().params;
  const { data: customerCredit } = useCustomerCredits();

  // const hasEditPermission = role === "cto" || userId === data?.admin_id;
  const hasEditPermission =
    chmod("customer", "PUT") &&
    (role === strings.ceo || data?.admin_id === userId);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors: inputErrors },
  } = useForm<CustomerFormDataT>({
    // defaultValues: {
    //   customerCredit: null,
    //   buyStatus: null,
    //   buyAbility: "",
    //   bankAccountCreationDate: "",
    //   maxPassedCheque: "",
    //   bouncedChequeCount: "",
    //   creditByHerself: "",
    //   creditByCoWorkers: "",
    //   creditDescriptions: "",
    // },
  });

  useEffect(() => {
    if (data) {
      setValue("customerCredit", data?.credit?.status);
      setValue("buyStatus", data?.credit?.buy_type);
      setValue("buyAbility", data?.credit?.power_buy_monthly);
      setValue("bankAccountCreationDate", data?.credit?.open_account_at);
      setValue("maxPassedCheque", data?.credit?.passed_cheque_amount);
      setValue("bouncedChequeCount", data?.credit?.returned_cheque_count);
      setValue("creditByHerself", data?.credit?.search_description);
      setValue("creditByCoWorkers", data?.credit?.colleague_description);
      setValue("creditDescriptions", data?.credit?.description);
    }
  }, [data, setValue]);

  const editCustomerMut = useEditCustomer();
  const _onEdit = () =>
    editCustomerMut.mutate(
      {
        id: data?.id,
        data: { ...getPostData(getValues(), data?.id), section: "credit" },
      },
      { onSuccess: _goToBack }
    );

  return (
    <>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={css.responsiveInputsListContainer}
      >
        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.customer_credit} />
          <Controller
            name="customerCredit"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <Picker
                  disabled={!hasEditPermission}
                  onSelect={onChange}
                  value={value?.title ? value?.title : undefined}
                  data={customerCredit?.data?.credit_status || []}
                  icon={{ name: "bar-chart-2" }}
                  placeholder={strings.pick_customer_credit}
                />
              );
            }}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.buy_status} />
          {customerCredit?.data?.buy_status && (
            <Controller
              name="buyStatus"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <RadioButtonGroup
                    disabled={!hasEditPermission}
                    value={value}
                    selectedItem={onChange}
                    data={customerCredit?.data?.buy_status}
                  />
                  <View style={{ height: mq(20) }} />
                </>
              )}
            />
          )}
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel
            label={strings.buy_ability}
            desc={` (${strings.buy_ability_desc})`}
          />
          <Controller
            name="buyAbility"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                value={value ? String(value) : undefined}
                onChangeText={onChange}
                icon={{ name: "figma" }}
                keyboardType="number-pad"
                placeholder={strings.insert_buy_ability}
                error={inputErrors.buyAbility?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.bank_account_creation_date} />
          <Controller
            name="bankAccountCreationDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <View>
                <DatePicker
                  disabled={!hasEditPermission}
                  maximumDate={getFormatedDate(new Date(), "jYYYY/jMM/jDD h:m")}
                  value={value}
                  onSelect={onChange}
                  placeholder={strings.pick_bank_account_creation_date}
                />
                {getValues().bankAccountCreationDate !== null &&
                  getValues().bankAccountCreationDate !== "" && (
                    <Button
                      onPress={() => setValue("bankAccountCreationDate", null)}
                      icon={{
                        name: "close",
                        type: "antDesign",
                        size: 18,
                        color: colors.textColorGray,
                      }}
                      bgColor={colors.transparent}
                      width={mq(45)}
                      height={mq(42)}
                      absolute
                      left={30}
                    />
                  )}
              </View>
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel
            label={strings.max_passed_cheque}
            desc={` (${strings.currency})`}
          />
          <Controller
            name="maxPassedCheque"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                value={value ? String(value) : undefined}
                onChangeText={onChange}
                icon={{ name: "bar-chart" }}
                keyboardType="number-pad"
                placeholder={strings.insert_max_passed_cheque}
                enablePriceFormatter
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.bounced_cheque_count} />
          <Controller
            name="bouncedChequeCount"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                value={value ? String(value) : undefined}
                onChangeText={onChange}
                icon={{ name: "layers" }}
                keyboardType="number-pad"
                placeholder={strings.insert_bounced_cheque_count}
                error={inputErrors.bouncedChequeCount?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.credit_by_herself} />
          <Controller
            name="creditByHerself"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextArea
                editable={hasEditPermission}
                value={value}
                onChangeText={onChange}
                icon={{ name: "align-right" }}
                placeholder={strings.insert_credit_by_herself}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.credit_by_co_workers} />
          <Controller
            name="creditByCoWorkers"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextArea
                editable={hasEditPermission}
                value={value}
                onChangeText={onChange}
                icon={{ name: "align-right" }}
                placeholder={strings.insert_credit_by_co_workers}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.credit_descriptions} />
          <Controller
            name="creditDescriptions"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextArea
                editable={hasEditPermission}
                value={value}
                onChangeText={onChange}
                icon={{ name: "align-right" }}
                placeholder={strings.insert_credit_descriptions}
              />
            )}
          />
        </View>
      </ScrollView>
      {hasEditPermission && (
        <View
          style={{
            ...css.paddingH,
            marginVertical: sizes.space / 2,
            maxWidth: sn("100%", "100%", 300, 300),
          }}
        >
          <Button
            loading={editCustomerMut.isLoading}
            style={css.selfCenter}
            title={{ value: strings.submit_info }}
            onPress={handleSubmit(_onEdit)}
          />
        </View>
      )}
    </>
  );
}
